if (typeof ProductVariantGallery === 'undefined') {
  class ProductVariantGallery extends HTMLElement {
    static SELECTORS = {
      data: 'script[type="application/json"]',
      gallery: 'product-gallery',
      carousel: '.product-gallery__carousel',
      slide: '.product-gallery__media',
      firstVideoSlide: '.first-video',
      thumbnailsNav: 'carousel-navigation.scroll-marker-group--thumbnails',
      marker: '.scroll-marker',
      videoMarker: '[data-first-video-marker]'
    };

    static ATTRIBUTES = {
      initialVariantId: 'initial-variant-id',
      form: 'form',
      filteredIndexes: 'filtered-indexes',
      current: 'aria-current'
    };

    static EVENTS = { variantChange: 'variant:change' };
    static CONFIGURATOR_HASH = '#configurator';
    static FADE_MS = 200;

    connectedCallback () {
      this._abort = new AbortController();
      this._gallery = this.closest(ProductVariantGallery.SELECTORS.gallery);
      this._carousel = this._gallery?.querySelector(ProductVariantGallery.SELECTORS.carousel);
      if (!this._carousel) return;

      this._galleries = this._parseGalleries();
      this._initialVariantId = this.getAttribute(ProductVariantGallery.ATTRIBUTES.initialVariantId);
      this._listenVariantChange();
      this._applyInitial();
    }

    disconnectedCallback () {
      this._abort?.abort();
    }

    _parseGalleries () {
      try {
        return JSON.parse(this.querySelector(ProductVariantGallery.SELECTORS.data)?.textContent || '{}');
      } catch {
        return {};
      }
    }

    _listenVariantChange () {
      const form = document.forms[this._gallery.getAttribute(ProductVariantGallery.ATTRIBUTES.form)];
      form?.addEventListener(
        ProductVariantGallery.EVENTS.variantChange,
        (event) => this._apply(event.detail.variant?.id, { fade: true }),
        { signal: this._abort.signal }
      );
    }

    // In configurator mode no gender is picked yet: keep the mixed server gallery and drop the variant-option filter.
    _applyInitial () {
      if (window.location.hash === ProductVariantGallery.CONFIGURATOR_HASH) {
        this._clearVariantOptionFilter();
        return;
      }
      this._apply(this._initialVariantId, { fade: false });
    }

    _clearVariantOptionFilter () {
      this._gallery.setAttribute(ProductVariantGallery.ATTRIBUTES.filteredIndexes, '[]');
      this._carousel.querySelectorAll(`${ProductVariantGallery.SELECTORS.slide}[hidden]`).forEach((slide) => slide.removeAttribute('hidden'));
    }

    _apply (variantId, { fade }) {
      const gallery = this._galleries[variantId];
      if (!gallery?.media_html?.length) return;

      const commit = () => {
        this._renderSlides(gallery.media_html);
        this._renderThumbnails(gallery.thumbnails_html || []);
        this._carousel.select?.(0, { instant: true });
        this._carousel.style.opacity = 1;
      };

      if (!fade) {
        commit();
        return;
      }
      this._carousel.style.opacity = 0;
      setTimeout(commit, ProductVariantGallery.FADE_MS);
    }

    // The product-level metafield video is server-rendered first; keep it in front of the variant gallery.
    _renderSlides (mediaHtml) {
      const videoSlide = this._carousel
        .querySelector(ProductVariantGallery.SELECTORS.firstVideoSlide)
        ?.closest(ProductVariantGallery.SELECTORS.slide);
      this._carousel.innerHTML = mediaHtml.join('');
      if (videoSlide) this._carousel.prepend(videoSlide);
    }

    // Navigations cache their buttons on connect, so each one is replaced by a fresh element.
    _renderThumbnails (thumbnailsHtml) {
      const navs = this._gallery.querySelectorAll(
        `${ProductVariantGallery.SELECTORS.thumbnailsNav}[aria-controls="${this._carousel.id}"]`
      );

      navs.forEach((nav) => {
        const videoMarker = nav.querySelector(ProductVariantGallery.SELECTORS.videoMarker);
        const freshNav = nav.cloneNode(false);
        freshNav.innerHTML = thumbnailsHtml.join('');
        if (videoMarker) freshNav.prepend(videoMarker);
        freshNav.querySelectorAll(ProductVariantGallery.SELECTORS.marker).forEach((marker, index) =>
          marker.setAttribute(ProductVariantGallery.ATTRIBUTES.current, index === 0 ? 'true' : 'false')
        );
        nav.replaceWith(freshNav);
      });
    }
  }

  window.ProductVariantGallery = ProductVariantGallery;
}

if (!customElements.get('product-variant-gallery')) {
  customElements.define('product-variant-gallery', window.ProductVariantGallery);
}
